import React, { FC, useCallback, useMemo } from 'react'
import styled, { TypographyKeys } from '~/app/components/styled'
import { Typography } from '@yanolja-org/ya-com-core'
import usePreventDoubleEvent from '~/app/utils/usePreventDoubleEvent'
import useEventHooks from '~/app/components/common/useEventHooks'
import { Icon_arrowforward_fill } from '@yanolja-org/ya-com-system-icon'

/**
 * TextButton component
 * author: soojin.oh
 */

// type definitions

interface Type {
  transparent
  basic
  primary
}

type Props = {
  id?: string
  title: string | React.ReactNode
  type: keyof Type
  typography: Extract<TypographyKeys, 'subtitle3' | 'body1' | 'body2'>
  onClick?: (id: string | null, event: React.MouseEvent) => void
  preventDoubleTab?: {
    enabled: boolean
    delay: number
  }
  bold?: boolean
  disabled?: boolean
  className?: string
  testId?: string
  showArrow?: boolean
} & Pick<React.HTMLProps<HTMLButtonElement>, 'style'>

const getTypography = ({ size, bold }: { size: TypographyKeys; bold: boolean }) => {
  const typography = Typography(size)
  return bold ? typography.bold : typography.regular
}

// styled components
const Container = styled.button<{ size: TypographyKeys; bold: boolean }>`
  ${getTypography};

  padding: 0.8rem 0;

  &.transparent {
    color: ${({ theme: { color } }) => color.white};
    background: transparent;

    @media (hover: hover) and (pointer: fine) {
      :hover {
        background: ${({ theme: { color } }) => color.white_op10};
      }
    }

    // pressed
    :active {
      background: ${({ theme: { color } }) => color.white_op20};
    }

    &.disabled {
      color: ${({ theme: { color } }) => color.gray600};
      background: ${({ theme: { color } }) => color.white};
    }
  }

  &.basic {
    color: ${({ theme: { color } }) => color.gray900};
    background: ${({ theme: { color } }) => color.white};

    @media (hover: hover) and (pointer: fine) {
      :hover {
        background: ${({ theme: { color } }) => color.black_op5};
      }
    }

    // pressed
    :active {
      background: ${({ theme: { color } }) => color.black_op10};
    }

    &.disabled {
      color: ${({ theme: { color } }) => color.gray400};
      background: ${({ theme: { color } }) => color.transparent};
    }
  }

  &.primary {
    color: ${({ theme: { color } }) => color.blue600};
    background: ${({ theme: { color } }) => color.white};

    @media (hover: hover) and (pointer: fine) {
      :hover {
        background: ${({ theme: { color } }) => color.blue50};
      }
    }

    // pressed
    :active {
      background: ${({ theme: { color } }) => color.blue100};
    }

    &.disabled {
      color: ${({ theme: { color } }) => color.gray400};
      background: ${({ theme: { color } }) => color.transparent};
    }
  }
`

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const ArrowIcon = styled(Icon_arrowforward_fill)`
  position: relative;
  padding-left: 0.2rem;
  path {
    fill: ${({ theme: { color } }) => color.gray900};
  }
`

// TextButton component
const TextButton: FC<Props> = function ({
  id,
  title,
  type,
  typography,
  bold = false,
  disabled = false,
  preventDoubleTab = {
    enabled: true,
    delay: 1000,
  },
  onClick,
  className,
  style,
  testId,
  showArrow = false,
}) {
  const newClassName = useMemo(
    () => [className, type, disabled ? 'disabled' : ''].join(' '),
    [className]
  )
  const onClickCallback = useCallback(
    event => !disabled && onClick?.(id ?? null, event),
    [disabled, onClick, id]
  )
  const delay = preventDoubleTab?.enabled ? preventDoubleTab.delay : 0
  const [onButtonClick] = usePreventDoubleEvent(onClickCallback, delay)
  const { stop } = useEventHooks()

  const iconSize = useMemo(() => {
    if (typography === 'subtitle3') {
      return 1.4
    } else if (typography === 'body1') {
      return 1.2
    } else {
      return 1
    }
  }, [typography])

  return (
    <Container
      size={typography}
      bold={bold}
      className={newClassName}
      style={style}
      onClick={stop(onButtonClick)}
      data-testid={testId || 'text-button'}
    >
      <Content>
        <span>{title}</span>
        {showArrow && <ArrowIcon size={iconSize} unit="rem" />}
      </Content>
    </Container>
  )
}

export default TextButton
